import React, { useEffect, useRef, useState, useCallback } from "react";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const W = 480;
const H = 300;
const GROUND_Y = H - 30;
const GRAVITY = 0.35;
const PROJECTILE_R = 14;
const SLING_X = 90;
const SLING_Y = GROUND_Y - 70;
const MAX_DRAG = 70;
const LAUNCH_POWER = 0.18;

// Block types
const BLOCK_TYPES = [
  { emoji: "🧱", w: 38, h: 38, hp: 3, points: 10 },
  { emoji: "🪵", w: 38, h: 28, hp: 2, points: 8 },
  { emoji: "🧊", w: 28, h: 28, hp: 1, points: 5 },
];

// Projectile types (piatti da lanciare)
const PROJECTILE_TYPES = [
  { emoji: "🍕", r: 14 },
  { emoji: "🍳", r: 14 },
  { emoji: "🥘", r: 16 },
  { emoji: "🧆", r: 12 },
];

// Target enemies
const ENEMY_TYPES = [
  { emoji: "🐷", r: 16, points: 50 },
  { emoji: "👨‍🍳", r: 16, points: 30 },
];

// ─── LEVELS ──────────────────────────────────────────────────────────────────
// G = GROUND_Y alias for readability
const G = GROUND_Y;
const LEVELS = [
  // ── 1 · Tutorial: singolo nemico, nessun blocco ─────────────────────────
  {
    projectiles: ["🍕", "🍕"],
    blocks: [],
    enemies: [{ type: 0, x: 340, y: G - 16 }],
  },
  // ── 2 · Muro basso con nemico sopra ──────────────────────────────────────
  {
    projectiles: ["🍕", "🍕", "🍳"],
    blocks: [
      { type: 0, x: 320, y: G - 38 },
      { type: 0, x: 358, y: G - 38 },
      { type: 2, x: 339, y: G - 66 },
    ],
    enemies: [{ type: 0, x: 339, y: G - 95 }],
  },
  // ── 3 · Torre con due nemici ai lati ─────────────────────────────────────
  {
    projectiles: ["🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 300, y: G - 38 }, { type: 0, x: 338, y: G - 38 }, { type: 0, x: 376, y: G - 38 },
      { type: 1, x: 319, y: G - 66 }, { type: 1, x: 357, y: G - 66 },
      { type: 2, x: 338, y: G - 90 },
    ],
    enemies: [
      { type: 0, x: 319, y: G - 100 },
      { type: 1, x: 357, y: G - 100 },
    ],
  },
  // ── 4 · Due torri separate ────────────────────────────────────────────────
  {
    projectiles: ["🍕", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 2, x: 290, y: G - 28 }, { type: 2, x: 290, y: G - 56 },
      { type: 2, x: 400, y: G - 28 }, { type: 2, x: 400, y: G - 56 },
    ],
    enemies: [
      { type: 0, x: 290, y: G - 84 },
      { type: 0, x: 400, y: G - 84 },
    ],
  },
  // ── 5 · Piramide a 3 piani ────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🍕", "🍳"],
    blocks: [
      { type: 0, x: 290, y: G - 38 }, { type: 0, x: 328, y: G - 38 }, { type: 0, x: 366, y: G - 38 }, { type: 0, x: 404, y: G - 38 },
      { type: 1, x: 309, y: G - 66 }, { type: 1, x: 347, y: G - 66 }, { type: 1, x: 385, y: G - 66 },
      { type: 2, x: 328, y: G - 90 }, { type: 2, x: 366, y: G - 90 },
      { type: 2, x: 347, y: G - 114 },
    ],
    enemies: [
      { type: 0, x: 309, y: G - 100 },
      { type: 0, x: 385, y: G - 100 },
      { type: 1, x: 347, y: G - 142 },
    ],
  },
  // ── 6 · Bunker: nemico dentro una gabbia di blocchi forti ─────────────────
  {
    projectiles: ["🥘", "🥘", "🥘", "🍕"],
    blocks: [
      { type: 0, x: 330, y: G - 38 }, { type: 0, x: 368, y: G - 38 },
      { type: 0, x: 330, y: G - 76 }, { type: 0, x: 368, y: G - 76 },
      { type: 1, x: 349, y: G - 110 },
    ],
    enemies: [{ type: 0, x: 349, y: G - 52 }],
  },
  // ── 7 · Fila lunga di blocchi leggeri ────────────────────────────────────
  {
    projectiles: ["🍕", "🍳", "🍕", "🍳"],
    blocks: [
      { type: 2, x: 270, y: G - 28 }, { type: 2, x: 298, y: G - 28 },
      { type: 2, x: 326, y: G - 28 }, { type: 2, x: 354, y: G - 28 },
      { type: 2, x: 382, y: G - 28 }, { type: 2, x: 410, y: G - 28 },
    ],
    enemies: [
      { type: 1, x: 298, y: G - 56 },
      { type: 0, x: 382, y: G - 56 },
    ],
  },
  // ── 8 · Castello con torre centrale alta ─────────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 280, y: G - 38 }, { type: 0, x: 318, y: G - 38 }, { type: 0, x: 380, y: G - 38 }, { type: 0, x: 418, y: G - 38 },
      { type: 1, x: 280, y: G - 66 }, { type: 1, x: 418, y: G - 66 },
      { type: 0, x: 349, y: G - 38 }, { type: 0, x: 349, y: G - 76 }, { type: 0, x: 349, y: G - 114 },
    ],
    enemies: [
      { type: 0, x: 280, y: G - 94 },
      { type: 0, x: 418, y: G - 94 },
      { type: 1, x: 349, y: G - 142 },
    ],
  },
  // ── 9 · Tre torri equidistanti ────────────────────────────────────────────
  {
    projectiles: ["🍕", "🥘", "🍳", "🥘", "🍕"],
    blocks: [
      { type: 2, x: 280, y: G - 28 }, { type: 0, x: 280, y: G - 56 }, { type: 0, x: 280, y: G - 84 },
      { type: 2, x: 349, y: G - 28 }, { type: 0, x: 349, y: G - 56 },
      { type: 2, x: 418, y: G - 28 }, { type: 0, x: 418, y: G - 56 }, { type: 0, x: 418, y: G - 84 },
    ],
    enemies: [
      { type: 1, x: 280, y: G - 110 },
      { type: 0, x: 349, y: G - 84 },
      { type: 1, x: 418, y: G - 110 },
    ],
  },
  // ── 10 · Ponte: blocchi orizzontali con nemici sotto ─────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🍕", "🍕"],
    blocks: [
      { type: 0, x: 295, y: G - 38 }, { type: 0, x: 295, y: G - 76 },
      { type: 0, x: 435, y: G - 38 }, { type: 0, x: 435, y: G - 76 },
      { type: 1, x: 315, y: G - 104 }, { type: 1, x: 349, y: G - 104 }, { type: 1, x: 383, y: G - 104 }, { type: 1, x: 417, y: G - 104 },
    ],
    enemies: [
      { type: 0, x: 349, y: G - 52 },
      { type: 1, x: 295, y: G - 104 },
      { type: 0, x: 435, y: G - 104 },
    ],
  },
  // ── 11 · Zigzag di blocchi ────────────────────────────────────────────────
  {
    projectiles: ["🍕", "🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 2, x: 270, y: G - 28 },
      { type: 0, x: 300, y: G - 56 },
      { type: 2, x: 330, y: G - 28 },
      { type: 0, x: 360, y: G - 56 },
      { type: 2, x: 390, y: G - 28 },
      { type: 0, x: 420, y: G - 56 },
      { type: 2, x: 420, y: G - 84 },
    ],
    enemies: [
      { type: 0, x: 300, y: G - 84 },
      { type: 1, x: 360, y: G - 84 },
      { type: 0, x: 420, y: G - 110 },
    ],
  },
  // ── 12 · Fortezza con cortile interno ────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🥘", "🍕"],
    blocks: [
      { type: 0, x: 270, y: G - 38 }, { type: 0, x: 270, y: G - 76 },
      { type: 0, x: 430, y: G - 38 }, { type: 0, x: 430, y: G - 76 },
      { type: 1, x: 310, y: G - 104 }, { type: 1, x: 350, y: G - 104 }, { type: 1, x: 390, y: G - 104 },
      { type: 0, x: 270, y: G - 114 }, { type: 0, x: 430, y: G - 114 },
    ],
    enemies: [
      { type: 1, x: 310, y: G - 52 },
      { type: 0, x: 350, y: G - 52 },
      { type: 1, x: 390, y: G - 52 },
      { type: 0, x: 350, y: G - 130 },
    ],
  },
  // ── 13 · Scalinata ascendente ─────────────────────────────────────────────
  {
    projectiles: ["🍕", "🥘", "🍳", "🍕", "🥘"],
    blocks: [
      { type: 2, x: 270, y: G - 28 },
      { type: 2, x: 305, y: G - 28 }, { type: 2, x: 305, y: G - 56 },
      { type: 0, x: 340, y: G - 28 }, { type: 0, x: 340, y: G - 56 }, { type: 0, x: 340, y: G - 84 },
      { type: 0, x: 375, y: G - 28 }, { type: 0, x: 375, y: G - 56 }, { type: 0, x: 375, y: G - 84 }, { type: 0, x: 375, y: G - 112 },
      { type: 0, x: 410, y: G - 28 }, { type: 0, x: 410, y: G - 56 }, { type: 0, x: 410, y: G - 84 }, { type: 0, x: 410, y: G - 112 }, { type: 1, x: 410, y: G - 140 },
    ],
    enemies: [
      { type: 0, x: 270, y: G - 56 },
      { type: 1, x: 340, y: G - 110 },
      { type: 0, x: 410, y: G - 165 },
    ],
  },
  // ── 14 · Doppio bunker affiancato ─────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 285, y: G - 38 }, { type: 0, x: 323, y: G - 38 },
      { type: 0, x: 285, y: G - 76 }, { type: 0, x: 323, y: G - 76 },
      { type: 1, x: 304, y: G - 110 },
      { type: 0, x: 375, y: G - 38 }, { type: 0, x: 413, y: G - 38 },
      { type: 0, x: 375, y: G - 76 }, { type: 0, x: 413, y: G - 76 },
      { type: 1, x: 394, y: G - 110 },
    ],
    enemies: [
      { type: 0, x: 304, y: G - 52 },
      { type: 1, x: 304, y: G - 136 },
      { type: 0, x: 394, y: G - 52 },
      { type: 1, x: 394, y: G - 136 },
    ],
  },
  // ── 15 · Croce centrale ───────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍳", "🥘", "🍕"],
    blocks: [
      { type: 0, x: 349, y: G - 38 }, { type: 0, x: 349, y: G - 76 }, { type: 0, x: 349, y: G - 114 },
      { type: 1, x: 311, y: G - 76 }, { type: 1, x: 387, y: G - 76 },
      { type: 2, x: 273, y: G - 76 }, { type: 2, x: 425, y: G - 76 },
    ],
    enemies: [
      { type: 0, x: 349, y: G - 142 },
      { type: 1, x: 273, y: G - 104 },
      { type: 1, x: 425, y: G - 104 },
    ],
  },
  // ── 16 · Muro con finestrelle (nemici a quote diverse) ────────────────────
  {
    projectiles: ["🍕", "🥘", "🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 270, y: G - 38 }, { type: 0, x: 308, y: G - 38 },
      { type: 0, x: 384, y: G - 38 }, { type: 0, x: 422, y: G - 38 },
      { type: 0, x: 270, y: G - 76 }, { type: 0, x: 308, y: G - 76 }, { type: 0, x: 346, y: G - 76 }, { type: 0, x: 384, y: G - 76 }, { type: 0, x: 422, y: G - 76 },
      { type: 1, x: 346, y: G - 114 },
    ],
    enemies: [
      { type: 0, x: 289, y: G - 16 },
      { type: 1, x: 346, y: G - 52 },
      { type: 0, x: 403, y: G - 16 },
      { type: 1, x: 346, y: G - 140 },
    ],
  },
  // ── 17 · Doppia piramide ──────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍳", "🍕", "🥘"],
    blocks: [
      { type: 2, x: 268, y: G - 28 }, { type: 2, x: 296, y: G - 28 }, { type: 2, x: 324, y: G - 28 },
      { type: 0, x: 282, y: G - 56 }, { type: 0, x: 310, y: G - 56 },
      { type: 1, x: 296, y: G - 80 },
      { type: 2, x: 376, y: G - 28 }, { type: 2, x: 404, y: G - 28 }, { type: 2, x: 432, y: G - 28 },
      { type: 0, x: 390, y: G - 56 }, { type: 0, x: 418, y: G - 56 },
      { type: 1, x: 404, y: G - 80 },
    ],
    enemies: [
      { type: 0, x: 296, y: G - 106 },
      { type: 1, x: 350, y: G - 16 },
      { type: 0, x: 404, y: G - 106 },
    ],
  },
  // ── 18 · Labirinto orizzontale ────────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 0, x: 268, y: G - 38 }, { type: 0, x: 268, y: G - 76 }, { type: 0, x: 268, y: G - 114 },
      { type: 1, x: 306, y: G - 38 }, { type: 1, x: 344, y: G - 38 },
      { type: 1, x: 344, y: G - 76 }, { type: 1, x: 382, y: G - 76 },
      { type: 1, x: 382, y: G - 114 }, { type: 1, x: 420, y: G - 114 },
      { type: 0, x: 420, y: G - 38 }, { type: 0, x: 420, y: G - 76 },
    ],
    enemies: [
      { type: 0, x: 306, y: G - 76 },
      { type: 1, x: 344, y: G - 114 },
      { type: 0, x: 382, y: G - 52 },
      { type: 1, x: 420, y: G - 140 },
    ],
  },
  // ── 19 · Diamante ─────────────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 2, x: 349, y: G - 28 },
      { type: 1, x: 311, y: G - 56 }, { type: 1, x: 387, y: G - 56 },
      { type: 0, x: 273, y: G - 84 }, { type: 0, x: 349, y: G - 84 }, { type: 0, x: 425, y: G - 84 },
      { type: 1, x: 311, y: G - 112 }, { type: 1, x: 387, y: G - 112 },
      { type: 2, x: 349, y: G - 140 },
    ],
    enemies: [
      { type: 0, x: 311, y: G - 84 },
      { type: 1, x: 387, y: G - 84 },
      { type: 0, x: 349, y: G - 166 },
    ],
  },
  // ── 20 · Muro totale (grande assalto) ────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 0, x: 262, y: G - 38 }, { type: 0, x: 300, y: G - 38 }, { type: 0, x: 338, y: G - 38 }, { type: 0, x: 376, y: G - 38 }, { type: 0, x: 414, y: G - 38 },
      { type: 1, x: 262, y: G - 66 }, { type: 1, x: 300, y: G - 66 }, { type: 1, x: 338, y: G - 66 }, { type: 1, x: 376, y: G - 66 }, { type: 1, x: 414, y: G - 66 },
      { type: 0, x: 281, y: G - 94 }, { type: 0, x: 357, y: G - 94 },
      { type: 2, x: 319, y: G - 94 }, { type: 2, x: 395, y: G - 94 },
    ],
    enemies: [
      { type: 0, x: 262, y: G - 94 },
      { type: 1, x: 319, y: G - 120 },
      { type: 0, x: 395, y: G - 120 },
      { type: 1, x: 414, y: G - 94 },
    ],
  },
  // ── 21 · L rovesciata ────────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍕", "🍳", "🥘", "🍕"],
    blocks: [
      { type: 0, x: 280, y: G - 38 }, { type: 0, x: 280, y: G - 76 }, { type: 0, x: 280, y: G - 114 }, { type: 0, x: 280, y: G - 152 },
      { type: 1, x: 318, y: G - 38 }, { type: 1, x: 356, y: G - 38 }, { type: 1, x: 394, y: G - 38 },
      { type: 2, x: 394, y: G - 66 },
    ],
    enemies: [
      { type: 1, x: 318, y: G - 66 },
      { type: 0, x: 356, y: G - 66 },
      { type: 1, x: 394, y: G - 92 },
      { type: 0, x: 280, y: G - 180 },
    ],
  },
  // ── 22 · Isole sospese (blocchi a quote alte) ────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍳", "🍕", "🥘"],
    blocks: [
      { type: 0, x: 290, y: G - 38 },
      { type: 1, x: 290, y: G - 76 }, { type: 1, x: 328, y: G - 76 },
      { type: 0, x: 309, y: G - 110 }, { type: 0, x: 347, y: G - 110 },
      { type: 1, x: 385, y: G - 38 },
      { type: 1, x: 385, y: G - 76 }, { type: 1, x: 423, y: G - 76 },
      { type: 0, x: 404, y: G - 110 }, { type: 0, x: 442, y: G - 110 },
    ],
    enemies: [
      { type: 0, x: 309, y: G - 138 },
      { type: 1, x: 347, y: G - 16 },
      { type: 0, x: 404, y: G - 138 },
      { type: 1, x: 442, y: G - 16 },
    ],
  },
  // ── 23 · Spirale ─────────────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍕", "🥘", "🍳", "🍕", "🥘"],
    blocks: [
      { type: 0, x: 268, y: G - 38 }, { type: 0, x: 306, y: G - 38 }, { type: 0, x: 344, y: G - 38 }, { type: 0, x: 382, y: G - 38 }, { type: 0, x: 420, y: G - 38 },
      { type: 1, x: 420, y: G - 76 }, { type: 1, x: 420, y: G - 114 },
      { type: 1, x: 344, y: G - 114 }, { type: 1, x: 382, y: G - 114 },
      { type: 0, x: 344, y: G - 76 },
      { type: 0, x: 268, y: G - 76 }, { type: 0, x: 268, y: G - 114 }, { type: 0, x: 268, y: G - 152 },
      { type: 1, x: 306, y: G - 152 }, { type: 1, x: 344, y: G - 152 },
    ],
    enemies: [
      { type: 0, x: 344, y: G - 52 },
      { type: 1, x: 382, y: G - 52 },
      { type: 0, x: 344, y: G - 140 },
      { type: 1, x: 420, y: G - 140 },
    ],
  },
  // ── 24 · Quattro bunker angolari ─────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 0, x: 268, y: G - 38 }, { type: 0, x: 268, y: G - 76 }, { type: 1, x: 306, y: G - 76 },
      { type: 0, x: 420, y: G - 38 }, { type: 0, x: 420, y: G - 76 }, { type: 1, x: 382, y: G - 76 },
      { type: 0, x: 268, y: G - 114 }, { type: 0, x: 268, y: G - 152 }, { type: 1, x: 306, y: G - 114 },
      { type: 0, x: 420, y: G - 114 }, { type: 0, x: 420, y: G - 152 }, { type: 1, x: 382, y: G - 114 },
    ],
    enemies: [
      { type: 0, x: 287, y: G - 52 },
      { type: 0, x: 401, y: G - 52 },
      { type: 1, x: 287, y: G - 130 },
      { type: 1, x: 401, y: G - 130 },
      { type: 0, x: 344, y: G - 16 },
    ],
  },
  // ── 25 · Torre Eiffel ────────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🍕", "🥘", "🍳", "🥘", "🍕", "🥘"],
    blocks: [
      { type: 1, x: 290, y: G - 28 }, { type: 1, x: 410, y: G - 28 },
      { type: 0, x: 300, y: G - 56 }, { type: 0, x: 400, y: G - 56 },
      { type: 0, x: 315, y: G - 84 }, { type: 0, x: 385, y: G - 84 },
      { type: 1, x: 330, y: G - 112 }, { type: 1, x: 370, y: G - 112 },
      { type: 0, x: 340, y: G - 140 }, { type: 0, x: 360, y: G - 140 },
      { type: 2, x: 350, y: G - 168 },
    ],
    enemies: [
      { type: 0, x: 290, y: G - 54 },
      { type: 0, x: 410, y: G - 54 },
      { type: 1, x: 350, y: G - 16 },
      { type: 0, x: 350, y: G - 194 },
    ],
  },
  // ── 26 · Scacchiera 4×4 ──────────────────────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍕", "🥘", "🍳", "🥘", "🍕"],
    blocks: [
      { type: 2, x: 270, y: G - 28 }, { type: 0, x: 308, y: G - 28 }, { type: 2, x: 346, y: G - 28 }, { type: 0, x: 384, y: G - 28 }, { type: 2, x: 422, y: G - 28 },
      { type: 0, x: 270, y: G - 56 }, { type: 2, x: 308, y: G - 56 }, { type: 0, x: 346, y: G - 56 }, { type: 2, x: 384, y: G - 56 }, { type: 0, x: 422, y: G - 56 },
      { type: 2, x: 270, y: G - 84 }, { type: 0, x: 308, y: G - 84 }, { type: 2, x: 346, y: G - 84 }, { type: 0, x: 384, y: G - 84 }, { type: 2, x: 422, y: G - 84 },
      { type: 0, x: 270, y: G - 112 }, { type: 2, x: 346, y: G - 112 }, { type: 0, x: 422, y: G - 112 },
    ],
    enemies: [
      { type: 1, x: 308, y: G - 112 },
      { type: 0, x: 384, y: G - 112 },
      { type: 1, x: 346, y: G - 140 },
    ],
  },
  // ── 27 · Città (edifici a quote diverse) ─────────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 265, y: G - 38 }, { type: 0, x: 265, y: G - 76 }, { type: 0, x: 265, y: G - 114 }, { type: 1, x: 265, y: G - 142 },
      { type: 2, x: 303, y: G - 28 }, { type: 2, x: 303, y: G - 56 },
      { type: 0, x: 341, y: G - 38 }, { type: 0, x: 341, y: G - 76 }, { type: 0, x: 341, y: G - 114 }, { type: 0, x: 341, y: G - 152 }, { type: 1, x: 341, y: G - 180 },
      { type: 2, x: 379, y: G - 28 }, { type: 2, x: 379, y: G - 56 }, { type: 2, x: 379, y: G - 84 },
      { type: 0, x: 417, y: G - 38 }, { type: 0, x: 417, y: G - 76 }, { type: 1, x: 417, y: G - 104 },
    ],
    enemies: [
      { type: 0, x: 265, y: G - 168 },
      { type: 1, x: 303, y: G - 82 },
      { type: 0, x: 341, y: G - 206 },
      { type: 1, x: 379, y: G - 110 },
      { type: 0, x: 417, y: G - 130 },
    ],
  },
  // ── 28 · Doppia fortezza con ponte centrale ───────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳"],
    blocks: [
      { type: 0, x: 265, y: G - 38 }, { type: 0, x: 265, y: G - 76 }, { type: 0, x: 265, y: G - 114 },
      { type: 0, x: 303, y: G - 38 }, { type: 0, x: 303, y: G - 76 }, { type: 0, x: 303, y: G - 114 },
      { type: 1, x: 341, y: G - 114 }, { type: 1, x: 379, y: G - 114 },
      { type: 0, x: 417, y: G - 38 }, { type: 0, x: 417, y: G - 76 }, { type: 0, x: 417, y: G - 114 },
      { type: 0, x: 455, y: G - 38 }, { type: 0, x: 455, y: G - 76 }, { type: 0, x: 455, y: G - 114 },
    ],
    enemies: [
      { type: 0, x: 265, y: G - 142 },
      { type: 1, x: 303, y: G - 142 },
      { type: 0, x: 341, y: G - 52 },
      { type: 1, x: 379, y: G - 52 },
      { type: 0, x: 417, y: G - 142 },
      { type: 1, x: 455, y: G - 142 },
    ],
  },
  // ── 29 · Labirinto a 3D (struttura complessa) ────────────────────────────
  {
    projectiles: ["🥘", "🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳", "🍕"],
    blocks: [
      { type: 0, x: 262, y: G - 38 }, { type: 0, x: 300, y: G - 38 }, { type: 0, x: 338, y: G - 38 }, { type: 0, x: 376, y: G - 38 }, { type: 0, x: 414, y: G - 38 },
      { type: 0, x: 262, y: G - 76 },                                    { type: 0, x: 338, y: G - 76 },                                   { type: 0, x: 414, y: G - 76 },
      { type: 1, x: 262, y: G - 114 }, { type: 1, x: 300, y: G - 114 }, { type: 1, x: 338, y: G - 114 }, { type: 1, x: 376, y: G - 114 }, { type: 1, x: 414, y: G - 114 },
      { type: 0, x: 262, y: G - 152 },                                   { type: 0, x: 338, y: G - 152 },                                   { type: 0, x: 414, y: G - 152 },
      { type: 2, x: 262, y: G - 190 }, { type: 2, x: 300, y: G - 190 }, { type: 2, x: 338, y: G - 190 }, { type: 2, x: 376, y: G - 190 }, { type: 2, x: 414, y: G - 190 },
    ],
    enemies: [
      { type: 0, x: 300, y: G - 76 },
      { type: 1, x: 376, y: G - 76 },
      { type: 0, x: 300, y: G - 152 },
      { type: 1, x: 376, y: G - 152 },
      { type: 0, x: 338, y: G - 216 },
    ],
  },
  // ── 30 · Boss finale: mega fortezza con 6 nemici ─────────────────────────
  {
    projectiles: ["🥘", "🥘", "🥘", "🍳", "🥘", "🍕", "🥘", "🍳", "🥘", "🍕"],
    blocks: [
      // base larga
      { type: 0, x: 255, y: G - 38 }, { type: 0, x: 293, y: G - 38 }, { type: 0, x: 331, y: G - 38 }, { type: 0, x: 369, y: G - 38 }, { type: 0, x: 407, y: G - 38 }, { type: 0, x: 445, y: G - 38 },
      // piano 2
      { type: 1, x: 255, y: G - 76 }, { type: 0, x: 293, y: G - 76 }, { type: 0, x: 369, y: G - 76 }, { type: 1, x: 445, y: G - 76 },
      // piano 3
      { type: 0, x: 255, y: G - 114 }, { type: 0, x: 331, y: G - 114 }, { type: 0, x: 407, y: G - 114 }, { type: 0, x: 445, y: G - 114 },
      // piano 4
      { type: 1, x: 293, y: G - 152 }, { type: 1, x: 369, y: G - 152 },
      // torre centrale
      { type: 0, x: 350, y: G - 76 }, { type: 0, x: 350, y: G - 114 }, { type: 0, x: 350, y: G - 152 }, { type: 0, x: 350, y: G - 190 }, { type: 1, x: 350, y: G - 228 },
    ],
    enemies: [
      { type: 0, x: 255, y: G - 52 },
      { type: 1, x: 445, y: G - 52 },
      { type: 0, x: 293, y: G - 180 },
      { type: 1, x: 407, y: G - 142 },
      { type: 0, x: 350, y: G - 16 },
      { type: 1, x: 350, y: G - 254 },
    ],
  },
];

// ─── EMOJI CANVAS CACHE ───────────────────────────────────────────────────────
const emojiCache = {};
const getEmoji = (emoji, size = 40) => {
  const key = `${emoji}_${size}`;
  if (emojiCache[key]) return emojiCache[key];
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d");
  ctx.font = `${size * 0.75}px serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(emoji, size / 2, size / 2);
  emojiCache[key] = c;
  return c;
};

// ─── PHYSICS HELPERS ─────────────────────────────────────────────────────────
const circleRect = (cx, cy, cr, rx, ry, rw, rh) => {
  const nearX = Math.max(rx, Math.min(cx, rx + rw));
  const nearY = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - nearX, dy = cy - nearY;
  return dx * dx + dy * dy < cr * cr;
};

const circleCircle = (x1, y1, r1, x2, y2, r2) => {
  const dx = x1 - x2, dy = y1 - y2;
  return dx * dx + dy * dy < (r1 + r2) * (r1 + r2);
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function AngryChefGame({ onBack }) {
  const canvasRef  = useRef(null);
  const stateRef   = useRef(null);
  const rafRef     = useRef(null);
  const dragRef    = useRef(null); // { x, y } current drag position (canvas coords)
  const isDragging = useRef(false);

  const [phase, setPhase]   = useState("idle");      // idle | aiming | flying | settling | win | lose | nextlevel
  const [levelIdx, setLevelIdx] = useState(0);
  const [score, setScore]   = useState(0);
  const [ammo, setAmmo]     = useState(0);
  const [message, setMessage] = useState("");

  // ── Build level state ────────────────────────────────────────────────────
  const buildLevel = useCallback((idx) => {
    const lvl = LEVELS[idx];
    return {
      projectile: null,
      trail: [],
      blocks: lvl.blocks.map((b, i) => ({
        ...BLOCK_TYPES[b.type],
        x: b.x, y: b.y, id: i,
        vx: 0, vy: 0, dead: false,
        angle: 0,
      })),
      enemies: lvl.enemies.map((e, i) => ({
        ...ENEMY_TYPES[e.type],
        x: e.x, y: e.y, id: i,
        vx: 0, vy: 0, dead: false,
        hit: false,
      })),
      ammoQueue: [...lvl.projectiles],
      score: 0,
      settling: 0,
      phase: "aiming",
    };
  }, []);

  const initLevel = useCallback((idx) => {
    const s = buildLevel(idx);
    stateRef.current = s;
    setAmmo(s.ammoQueue.length);
    setPhase("aiming");
    setMessage("");
  }, [buildLevel]);

  useEffect(() => { initLevel(0); }, [initLevel]);

  // ── Canvas pointer events ─────────────────────────────────────────────────
  const toCanvas = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = W / rect.width;
    const scaleY = H / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const s = stateRef.current;
    if (!s || s.phase !== "aiming" || s.ammoQueue.length === 0) return;
    const pt = toCanvas(e.clientX ?? e.touches[0].clientX, e.clientY ?? e.touches[0].clientY);
    const dx = pt.x - SLING_X, dy = pt.y - SLING_Y;
    if (Math.sqrt(dx * dx + dy * dy) < 40) {
      isDragging.current = true;
      dragRef.current = { x: SLING_X, y: SLING_Y };
    }
  };

  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const pt = toCanvas(e.clientX ?? e.touches[0].clientX, e.clientY ?? e.touches[0].clientY);
    let dx = pt.x - SLING_X, dy = pt.y - SLING_Y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > MAX_DRAG) { dx *= MAX_DRAG / dist; dy *= MAX_DRAG / dist; }
    dragRef.current = { x: SLING_X + dx, y: SLING_Y + dy };
  };

  const onPointerUp = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const s = stateRef.current;
    if (!s || s.phase !== "aiming") return;

    const drag = dragRef.current;
    const dx = SLING_X - drag.x;
    const dy = SLING_Y - drag.y;

    if (Math.sqrt(dx * dx + dy * dy) < 10) { dragRef.current = null; return; }

    // launch!
    const emoji = s.ammoQueue.shift();
    s.projectile = {
      x: drag.x, y: drag.y,
      vx: dx * LAUNCH_POWER,
      vy: dy * LAUNCH_POWER,
      emoji,
      r: PROJECTILE_R,
      trail: [],
      angle: 0,
    };
    s.phase = "flying";
    setPhase("flying");
    setAmmo(s.ammoQueue.length);
    dragRef.current = null;
  };

  // ── Game loop ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const s = stateRef.current;
      draw(ctx, s);
      if (!s) return;

      if (s.phase === "flying") {
        // update projectile
        const p = s.projectile;
        p.vy += GRAVITY;
        p.x += p.vx;
        p.y += p.vy;
        p.angle += 0.15;
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 18) p.trail.shift();

        // hit ground
        if (p.y + p.r >= GROUND_Y) {
          p.y = GROUND_Y - p.r;
          finishProjectile(s);
          return;
        }
        // out of bounds
        if (p.x > W + 20 || p.x < -20) {
          finishProjectile(s);
          return;
        }

        // hit blocks
        for (const b of s.blocks) {
          if (b.dead) continue;
          if (circleRect(p.x, p.y, p.r, b.x - b.w / 2, b.y - b.h, b.w, b.h)) {
            b.hp--;
            b.vx = p.vx * 0.4;
            b.vy = p.vy * 0.3 - 2;
            if (b.hp <= 0) { b.dead = true; s.score += b.points; setScore(prev => prev + b.points); }
            finishProjectile(s);
            return;
          }
        }

        // hit enemies
        for (const en of s.enemies) {
          if (en.dead) continue;
          if (circleCircle(p.x, p.y, p.r, en.x, en.y, en.r)) {
            en.dead = true;
            en.hit = true;
            s.score += en.points;
            setScore(prev => prev + en.points);
            finishProjectile(s);
            return;
          }
        }
      }

      if (s.phase === "settling") {
        // simple block gravity + settle
        let moving = false;
        for (const b of s.blocks) {
          if (b.dead) continue;
          b.vy += GRAVITY * 0.5;
          b.x += b.vx;
          b.y += b.vy;
          b.vx *= 0.85;
          if (b.y >= GROUND_Y) { b.y = GROUND_Y; b.vy = 0; b.vx *= 0.5; }
          if (Math.abs(b.vx) > 0.1 || Math.abs(b.vy) > 0.1) moving = true;
        }

        s.settling++;
        if (!moving || s.settling > 90) {
          checkEndCondition(s);
        }
      }
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const finishProjectile = (s) => {
    s.projectile = null;
    s.phase = "settling";
    s.settling = 0;
  };

  const checkEndCondition = (s) => {
    const allEnemiesDead = s.enemies.every(e => e.dead);
    if (allEnemiesDead) {
      // bonus points for remaining ammo
      const bonus = s.ammoQueue.length * 100;
      s.score += bonus;
      setScore(prev => prev + bonus);
      const nextIdx = levelIdx + 1;
      if (nextIdx < LEVELS.length) {
        setPhase("nextlevel");
        setMessage(`Livello completato! +${bonus} bonus ammo`);
        s.phase = "nextlevel";
      } else {
        setPhase("win");
        setMessage(`Hai vinto! Punteggio totale: ${s.score + bonus}`);
        s.phase = "win";
      }
      return;
    }
    if (s.ammoQueue.length === 0 && !s.projectile) {
      setPhase("lose");
      setMessage("Hai finito i piatti! Riprova.");
      s.phase = "lose";
      return;
    }
    s.phase = "aiming";
    setPhase("aiming");
  };

  // ── Draw ─────────────────────────────────────────────────────────────────
  const draw = (ctx, s) => {
    // sky
    const sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, "#87ceeb");
    sky.addColorStop(1, "#d4f0ff");
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, H);

    // clouds
    [[80, 40, 1], [200, 25, 0.8], [370, 55, 1.2]].forEach(([x, y, s]) => {
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.beginPath(); ctx.ellipse(x, y, 35 * s, 18 * s, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(x - 22 * s, y + 4 * s, 20 * s, 13 * s, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(x + 22 * s, y + 4 * s, 20 * s, 13 * s, 0, 0, Math.PI * 2); ctx.fill();
    });

    // ground
    const ground = ctx.createLinearGradient(0, GROUND_Y, 0, H);
    ground.addColorStop(0, "#5a9e3a");
    ground.addColorStop(0.15, "#4a8a2e");
    ground.addColorStop(1, "#3a6e22");
    ctx.fillStyle = ground;
    ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);

    // ground line
    ctx.strokeStyle = "#3a7a28";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, GROUND_Y); ctx.lineTo(W, GROUND_Y); ctx.stroke();

    // slingshot
    drawSlingshot(ctx);

    if (!s) return;

    // trajectory preview while dragging
    if (isDragging.current && dragRef.current && s.phase === "aiming") {
      const drag = dragRef.current;
      const vx0 = (SLING_X - drag.x) * LAUNCH_POWER;
      const vy0 = (SLING_Y - drag.y) * LAUNCH_POWER;
      ctx.setLineDash([4, 6]);
      ctx.strokeStyle = "rgba(255,100,0,0.5)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      let px = drag.x, py = drag.y, pvx = vx0, pvy = vy0;
      for (let i = 0; i < 40; i++) {
        pvy += GRAVITY;
        px += pvx; py += pvy;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        if (py > GROUND_Y) break;
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // blocks
    for (const b of s.blocks) {
      if (b.dead) continue;
      ctx.save();
      ctx.translate(b.x, b.y - b.h / 2);
      const img = getEmoji(b.emoji, Math.max(b.w, b.h));
      ctx.drawImage(img, -b.w / 2, -b.h / 2, b.w, b.h);
      // hp indicator
      if (b.hp > 1) {
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(-b.w / 2, b.h / 2 - 5, b.w, 4);
        ctx.fillStyle = b.hp >= 3 ? "#4caf50" : "#ff9800";
        ctx.fillRect(-b.w / 2, b.h / 2 - 5, b.w * (b.hp / BLOCK_TYPES[0].hp), 4);
      }
      ctx.restore();
    }

    // enemies
    for (const en of s.enemies) {
      if (en.dead) continue;
      ctx.save();
      ctx.translate(en.x, en.y);
      if (en.hit) {
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = "#ff0";
        ctx.beginPath(); ctx.arc(0, 0, en.r + 4, 0, Math.PI * 2); ctx.fill();
      }
      const img = getEmoji(en.emoji, en.r * 2 + 8);
      ctx.drawImage(img, -en.r - 4, -en.r - 4, en.r * 2 + 8, en.r * 2 + 8);
      ctx.restore();
    }

    // projectile trail
    if (s.projectile) {
      const p = s.projectile;
      p.trail.forEach((pt, i) => {
        ctx.globalAlpha = (i / p.trail.length) * 0.4;
        ctx.fillStyle = "#ff6600";
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, (i / p.trail.length) * p.r * 0.7, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      // draw projectile
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      const img = getEmoji(p.emoji, p.r * 2 + 6);
      ctx.drawImage(img, -p.r - 3, -p.r - 3, p.r * 2 + 6, p.r * 2 + 6);
      ctx.restore();
    }

    // sling rubber bands over projectile (when aiming)
    if (s.phase === "aiming" && isDragging.current && dragRef.current) {
      const drag = dragRef.current;
      ctx.strokeStyle = "#8B4513";
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(SLING_X - 8, SLING_Y - 10); ctx.lineTo(drag.x, drag.y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(SLING_X + 8, SLING_Y - 10); ctx.lineTo(drag.x, drag.y); ctx.stroke();

      // projectile on sling
      const emoji = s.ammoQueue[0] ?? "🍕";
      const img = getEmoji(emoji, PROJECTILE_R * 2 + 6);
      ctx.drawImage(img, drag.x - PROJECTILE_R - 3, drag.y - PROJECTILE_R - 3, PROJECTILE_R * 2 + 6, PROJECTILE_R * 2 + 6);
    } else if (s.phase === "aiming" && s.ammoQueue.length > 0) {
      // idle projectile on sling
      const emoji = s.ammoQueue[0];
      const img = getEmoji(emoji, PROJECTILE_R * 2 + 6);
      ctx.drawImage(img, SLING_X - PROJECTILE_R - 3, SLING_Y - PROJECTILE_R - 3, PROJECTILE_R * 2 + 6, PROJECTILE_R * 2 + 6);
    }

    // ammo queue display
    if (s.ammoQueue.length > 0) {
      s.ammoQueue.slice(1).forEach((emoji, i) => {
        const img = getEmoji(emoji, 26);
        ctx.drawImage(img, SLING_X - 30 + i * 28, GROUND_Y + 2, 26, 26);
      });
    }

    // HUD score
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(W - 110, 6, 104, 22);
    ctx.fillStyle = "#ffe033";
    ctx.font = "bold 11px 'Press Start 2P', monospace";
    ctx.textAlign = "right";
    ctx.fillText(`${s.score} pts`, W - 10, 22);
  };

  const drawSlingshot = (ctx) => {
    // Y-shape slingshot
    ctx.strokeStyle = "#8B4513";
    ctx.lineWidth = 6;
    ctx.lineCap = "round";
    // trunk
    ctx.beginPath(); ctx.moveTo(SLING_X, GROUND_Y); ctx.lineTo(SLING_X, SLING_Y + 10); ctx.stroke();
    // left fork
    ctx.beginPath(); ctx.moveTo(SLING_X, SLING_Y + 10); ctx.lineTo(SLING_X - 10, SLING_Y - 12); ctx.stroke();
    // right fork
    ctx.beginPath(); ctx.moveTo(SLING_X, SLING_Y + 10); ctx.lineTo(SLING_X + 10, SLING_Y - 12); ctx.stroke();
    // knobs
    ctx.fillStyle = "#6B3410";
    ctx.beginPath(); ctx.arc(SLING_X - 10, SLING_Y - 12, 4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(SLING_X + 10, SLING_Y - 12, 4, 0, Math.PI * 2); ctx.fill();
    // resting rubber (no drag)
    if (!isDragging.current) {
      ctx.strokeStyle = "#8B4513";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(SLING_X - 10, SLING_Y - 12); ctx.lineTo(SLING_X + 10, SLING_Y - 12); ctx.stroke();
    }
  };

  // ── Next level / restart ─────────────────────────────────────────────────
  const goNextLevel = () => {
    const next = levelIdx + 1;
    setLevelIdx(next);
    initLevel(next);
  };

  const restart = () => {
    setLevelIdx(0);
    setScore(0);
    initLevel(0);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const AC = {
    bg: "#0a0a0f", surface: "rgba(255,255,255,0.04)", surfaceHover: "rgba(255,255,255,0.07)",
    border: "rgba(255,255,255,0.08)", text: "#f5f5f7",
    secondary: "rgba(245,245,247,0.55)", tertiary: "rgba(245,245,247,0.30)",
    green: "#30d158", orange: "#ff9f0a", accent: "#0a84ff", red: "#ff453a",
  };

  return (
    <div style={{ minHeight: "100vh", background: AC.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1.5rem", gap: "1rem", userSelect: "none", fontFamily: "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif" }}>

      {/* HUD */}
      <div style={{ display: "flex", gap: 0, background: AC.surface, border: `1px solid ${AC.border}`, borderRadius: 16, overflow: "hidden", width: "100%", maxWidth: W }}>
        {[
          { label: "Livello",   value: `${levelIdx + 1} / ${LEVELS.length}`, color: AC.text },
          { label: "Punteggio", value: score,                                  color: AC.green },
          { label: "Piatti",    value: ammo,                                   color: AC.orange },
        ].map((h, i) => (
          <div key={i} style={{ flex: 1, padding: "0.8rem 1rem", borderRight: i < 2 ? `1px solid ${AC.border}` : "none" }}>
            <p style={{ margin: "0 0 0.2rem", fontSize: "0.62rem", color: AC.secondary, fontWeight: 500 }}>{h.label}</p>
            <p style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: h.color, letterSpacing: "-0.5px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{h.value}</p>
          </div>
        ))}
      </div>

      {/* Canvas */}
      <div style={{ position: "relative", width: "100%", maxWidth: W, borderRadius: 18, overflow: "hidden", border: `1px solid ${AC.border}`, boxShadow: "0 12px 40px rgba(0,0,0,0.55)", cursor: "crosshair", touchAction: "none" }}
        onMouseDown={onPointerDown} onMouseMove={onPointerMove} onMouseUp={onPointerUp}
        onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
      >
        <canvas ref={canvasRef} width={W} height={H} style={{ display: "block", width: "100%" }} />

        {/* Overlay win/lose/nextlevel */}
        {(phase === "win" || phase === "lose" || phase === "nextlevel") && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,15,0.78)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: "#1c1c1e", border: `1px solid ${AC.border}`, borderRadius: 22, padding: "2rem 2.25rem", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", boxShadow: "0 24px 64px rgba(0,0,0,0.6)", maxWidth: 260 }}>
              <div style={{ fontSize: "2.5rem", lineHeight: 1 }}>
                {phase === "win" ? "🏆" : phase === "nextlevel" ? "⭐" : "😞"}
              </div>
              <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: AC.text }}>
                {phase === "win" ? "Hai vinto!" : phase === "nextlevel" ? "Livello superato!" : "Game Over"}
              </p>
              {message && <p style={{ margin: 0, fontSize: "0.75rem", color: AC.secondary, lineHeight: 1.5 }}>{message}</p>}
              {phase === "nextlevel" && (
                <button onClick={goNextLevel} style={{ padding: "0.65rem 1.4rem", background: AC.green, border: "none", borderRadius: 12, color: "#fff", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", width: "100%" }}>
                  Livello {levelIdx + 2}
                </button>
              )}
              {(phase === "win" || phase === "lose") && (
                <button onClick={restart} style={{ padding: "0.65rem 1.4rem", background: AC.accent, border: "none", borderRadius: 12, color: "#fff", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", width: "100%" }}>
                  Rigioca
                </button>
              )}
            </div>
          </div>
        )}

        {/* Hint */}
        {phase === "aiming" && (
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", fontSize: "0.72rem", color: "rgba(245,245,247,0.5)", background: "rgba(10,10,15,0.55)", padding: "4px 12px", borderRadius: 8, whiteSpace: "nowrap", backdropFilter: "blur(4px)" }}>
            Trascina il piatto sulla fionda
          </div>
        )}
      </div>

      <p style={{ margin: 0, fontSize: "0.72rem", color: AC.tertiary }}>Trascina dalla fionda per lanciare</p>

      {onBack && (
        <button onClick={onBack} style={{ padding: "0.62rem 1.25rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, color: "rgba(245,245,247,0.55)", fontSize: "0.82rem", fontWeight: 500, cursor: "pointer", fontFamily: "inherit", transition: "background 160ms ease" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.07)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
        >← Torna ai giochi</button>
      )}
    </div>
  );
}
